import React, { useEffect, useState } from "react"
import firebase from "firebase"
import { db, auth } from "../../../../utils/firebase"
import { CommentIcon, AlertIcon } from "../../../../utils/svgs"

import QuickActions from "./quick-actions"
import Composer from "./composer"
import ActivityEntry from "./entry"
import { COMMUNICATION_TYPES } from "./types"

// The activity feed reuses the styles defined alongside the enquiry
// page. Importing here means consumers (e.g. the booking notes/activity
// tab) get the styles regardless of whether the enquiry page bundle has
// already loaded.
import "../../enquiry.scss"

/**
 * Activity feed for a parent record (enquiry or booking). Subscribes to the
 * `${basePath}/${collectionName}` subcollection, shows quick-action buttons +
 * composer + chronological feed (newest first).
 *
 * `basePath` is the dotted Firestore path to the parent document, e.g.
 * `enquiries/{id}` or `bookings/{id}`. `showHeader` toggles the section
 * heading (callers that already have their own title can hide it).
 * `quickActionTypes` overrides the default enquiry set — bookings pass
 * `BOOKING_QUICK_ACTION_TYPES` to expose amendment / complaint / etc.
 *
 * Append-only: existing entries are not editable. Follow-up notes are added
 * as replies in the entry's /replies subcollection.
 */
export default function ActivityFeed({ basePath, showHeader = true, quickActionTypes, readOnly = false, showAllTypes = false, emptyMessage, collectionName = "activity" }) {
	const [entries, setEntries] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [composerType, setComposerType] = useState(null)

	useEffect(() => {
		if (!basePath) return
		const unsub = db
			.collection(`${basePath}/${collectionName}`)
			.orderBy("created", "desc")
			.onSnapshot(
				(snap) => {
					// Only show direct client communication in the Activity feed.
					// System events (status / tag / follow-up / quote / agent
					// changes) render in the Change log card instead.
					const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
					setEntries(showAllTypes ? all : all.filter((e) => COMMUNICATION_TYPES.includes(e.type)))
					setLoading(false)
					setError(null)
				},
				(err) => {
					console.error("Activity feed listener error:", err)
					setError(err.message || "Could not load activity feed.")
					setLoading(false)
				}
			)
		return () => unsub()
	}, [basePath, collectionName, showAllTypes])

	const saveEntry = async ({ body }) => {
		await db.collection(`${basePath}/${collectionName}`).add({
			type: composerType,
			body,
			user: auth.currentUser?.uid || null,
			created: firebase.firestore.FieldValue.serverTimestamp(),
		})
		setComposerType(null)
	}

	return (
		<section className="enquiry-activity">
			{showHeader && (
				<div className="enquiry-section-header">
					<h3 className="enquiry-section-title">Activity</h3>
				</div>
			)}

			<div className="enquiry-feed">
				{!readOnly && (
					<>
						<QuickActions activeType={composerType} onPick={setComposerType} types={quickActionTypes} />

						{composerType && (
							<Composer
								type={composerType}
								onSave={saveEntry}
								onCancel={() => setComposerType(null)}
								submitLabel="Log entry"
							/>
						)}
					</>
				)}

				{loading && (
					<div className="enquiry-feed-empty" role="status" aria-live="polite">
						<small>Loading activity…</small>
					</div>
				)}

				{!loading && error && (
					<div className="enquiry-feed-empty" role="alert">
						<span className="enquiry-feed-empty-icon" aria-hidden="true">
							<AlertIcon />
						</span>
						<small>Couldn't load activity: {error}</small>
					</div>
				)}

				{!loading && !error && entries.length === 0 && (
					<div className="enquiry-feed-empty">
						<span className="enquiry-feed-empty-icon" aria-hidden="true">
							<CommentIcon />
						</span>
						<small>{emptyMessage || "No activity yet — use a button above to log the first."}</small>
					</div>
				)}

				{!loading && !error && entries.length > 0 && (
					<ul className="enquiry-feed-list">
						{entries.map((entry) => (
							<ActivityEntry key={entry.id} basePath={basePath} entry={entry} readOnly={readOnly} collectionName={collectionName} />
						))}
					</ul>
				)}
			</div>
		</section>
	)
}
